/**
 * NOS-116: Fetches sections with import_source (import or ai_generated) for ImportReviewPanel.
 */
import { useState, useEffect, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import type { PageSection } from "@/templates/types"

export interface SectionWithPage {
  section: PageSection
  pageSlug: string
  pageTitle: string | null
}

export function useImportReviewSections(orgId: string | null) {
  const [sections, setSections] = useState<SectionWithPage[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const fetch = useCallback(async () => {
    if (!orgId) {
      setSections([])
      return
    }
    setIsLoading(true)
    const { data: sectionsData, error } = await supabase
      .from("page_sections")
      .select("id, section_type, order_index, content, page_id, org_id, is_visible")
      .eq("org_id", orgId)
      .order("order_index", { ascending: true })

    if (error) {
      setIsLoading(false)
      setSections([])
      return
    }

    const withImport = (sectionsData ?? []).filter((s) => {
      const content = (s as { content?: Record<string, unknown> }).content
      const src = content?.import_source as string | undefined
      return src === "import" || src === "ai_generated"
    })

    if (withImport.length === 0) {
      setSections([])
      setIsLoading(false)
      return
    }

    const pageIds = [...new Set(withImport.map((s) => (s as { page_id: string }).page_id))]
    const { data: pagesData } = await supabase
      .from("pages")
      .select("id, slug, title")
      .in("id", pageIds)

    const pagesMap = new Map(
      (pagesData ?? []).map((p) => [
        (p as { id: string }).id,
        {
          slug: (p as { slug: string }).slug,
          title: (p as { title: string | null }).title,
        },
      ])
    )

    const result: SectionWithPage[] = withImport.map((s) => {
      const row = s as { page_id: string } & PageSection
      const page = pagesMap.get(row.page_id)
      return {
        section: row as PageSection,
        pageSlug: page?.slug ?? "unknown",
        pageTitle: page?.title ?? null,
      }
    })

    setSections(result)
    setIsLoading(false)
  }, [orgId])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { sections, isLoading, refresh: fetch }
}
